/**
 * Доменные типы, общие для API, внутреннего приложения и публичного сайта.
 * Единственный источник правды по формам данных — держим здесь.
 */

// ─── Пользователи и роли ─────────────────────────────────────────────────────
// Сами роли и права живут в roles.ts

import type { CommissionType, Role } from './roles.js';

export interface User {
  id: string;
  login: string;
  fullName: string;
  role: Role;
  /** Персональная скидка (+) / наценка (−) к доставке, в процентах. −5 = скидка 5%. */
  deliveryDiscountPercent: number;
  isActive: boolean;
  createdAt: string;
}

// ─── Справочники лота ────────────────────────────────────────────────────────

export type Platform = 'copart' | 'iaai' | 'copart_uk' | 'copart_ca' | 'iaai_ca' | 'manheim';

export const PLATFORM_LABELS: Record<Platform, string> = {
  copart: 'Copart',
  iaai: 'IAAI',
  copart_uk: 'Copart UK',
  copart_ca: 'Copart Canada',
  iaai_ca: 'IAAI Canada',
  manheim: 'Manheim',
};

/**
 * Страна отправления. Влияет на логистику и на то, какие тарифы доставки
 * имеет смысл заводить: из Канады и Британии машины едут иначе, чем из США.
 */
export const PLATFORM_COUNTRY: Record<Platform, string> = {
  copart: 'США',
  iaai: 'США',
  copart_uk: 'Великобритания',
  copart_ca: 'Канада',
  iaai_ca: 'Канада',
  manheim: 'США',
};

/** Площадки, по которым система умеет читать публичную страницу лота. */
export const PLATFORMS_WITH_LOT_FETCH: Platform[] = [
  'copart',
  'iaai',
  'copart_uk',
  'copart_ca',
  'iaai_ca',
];

/** Вид авто — влияет на надбавку к доставке (тариф ищется по локации + виду). */
export type VehicleKind =
  | 'sedan'
  | 'suv'
  | 'pickup'
  | 'coupe'
  | 'minivan'
  | 'motorcycle'
  | 'truck'
  // Оверсайз — из ТЗ заказчика от 31.10: «седан, сув, овер сайз».
  // По виду авто ищется тариф доставки, поэтому список один на расчёт и сделку
  | 'oversize';

export const VEHICLE_KIND_LABELS: Record<VehicleKind, string> = {
  sedan: 'Седан',
  suv: 'Внедорожник',
  pickup: 'Пикап',
  coupe: 'Купе',
  minivan: 'Минивэн',
  motorcycle: 'Мотоцикл',
  truck: 'Грузовик',
  oversize: 'Оверсайз',
};

/**
 * Виды, которые предлагаем выбрать.
 *
 * Правки заказчика от 01.11: в работе используются только мото, седан, сув и
 * оверсайз — купе, минивэн, пикап и грузовик из выбора убраны. Сами значения
 * остаются в `VehicleKind` и в подписях: в базе лежат старые расчёты, тарифы и
 * сделки с этими видами, и без подписи они показались бы кодом вместо названия.
 */
export const VEHICLE_KIND_CHOICES: VehicleKind[] = ['motorcycle', 'sedan', 'suv', 'oversize'];

export type FuelType = 'petrol' | 'diesel' | 'electric' | 'hybrid';

export const FUEL_LABELS: Record<FuelType, string> = {
  petrol: 'Бензин',
  diesel: 'Дизель',
  electric: 'Электро',
  hybrid: 'Гибрид',
};

/** car_type в терминах baza-gai.com.ua */
export type CarType = 'car' | 'electric' | 'hybrid' | 'motorcycle' | 'truck';

export type Currency = 'USD' | 'EUR' | 'UAH';

// ─── Тарифы ──────────────────────────────────────────────────────────────────

export interface DeliveryTariff {
  id: string;
  platform: Platform;
  /** Штат / город / площадка, например «Texas» */
  location: string;
  vehicleKind: VehicleKind;
  amountUsd: number;
  isActive: boolean;
  updatedAt: string;
  updatedByName: string | null;
}

export interface AuctionFeeTariff {
  id: string;
  platform: Platform;
  bidFrom: number;
  /** null = «и выше» */
  bidTo: number | null;
  /** Фиксированный сбор в USD */
  feeAmount: number;
  /** Дополнительный процент от ставки, если площадка берёт процент (0 = нет) */
  feePercent: number;
  isActive: boolean;
  updatedAt: string;
  updatedByName: string | null;
}

// ─── Настройки (значения по умолчанию) ───────────────────────────────────────

export interface CalcSettings {
  /** Комплекс, USD */
  complex: number;
  /** Сертификация, USD */
  certification: number;
  /** Комиссия компании, USD */
  commission: number;
  /** Swift, % от (ставка + аукционный сбор) */
  swiftPercent: number;
  /** Страховка фрахта, % от ставки */
  freightInsurancePercent: number;
  /** Портовые расходы (хендлинг), USD — отдельно от доставки */
  portHandling: number;
  /**
   * Экологический сбор, USD.
   * По умолчанию 0: в ответе baza-gai.com.ua растаможка уже приходит итоговой
   * суммой платежей, и отдельная строка легко приводит к задвоению.
   * Включать осознанно.
   */
  ecoFee: number;
  /** Маржа по умолчанию, USD — скрытая строка, в клиентскую карточку не выводится */
  marginDefault: number;

  /**
   * Надбавка дилеру к доставке и к комплексу — общая для всех дилеров.
   *
   * ТЗ от 01.11: дилер не получает процент от дохода материнской компании.
   * Он покупает у неё логистику по розничной цене: тариф доставки плюс
   * надбавка, комплекс плюс надбавка, — и зарабатывает уже на своей цене
   * клиенту. Персональная скидка дилера вычитается из этой надбавки
   * (см. `CalcInput.dealer`), поэтому обе величины живут в одних единицах.
   *
   * Значения по умолчанию названы заказчиком: 300 $ на доставку, 200 $ на
   * комплекс.
   */
  dealerDeliveryMarkup: number;
  /** Режим надбавки к доставке: `MARKUP_USD` (0) или `MARKUP_PERCENT` (1) */
  dealerDeliveryMarkupMode: number;
  dealerComplexMarkup: number;
  dealerComplexMarkupMode: number;
}

/**
 * Режим надбавки числом, а не строкой: настройки лежат в таблице `settings`
 * с колонкой `value numeric`, и ради двух флагов заводить вторую таблицу
 * дороже, чем сравнить с нулём. Тот же приём уже применён к «скрывать
 * учебные данные».
 */
export const MARKUP_USD = 0;
export const MARKUP_PERCENT = 1;

/**
 * Надбавка к базовой сумме.
 *
 * Отрицательную надбавку не допускаем: скидка дилера может съесть надбавку
 * целиком, но не может сделать цену НИЖЕ нашей собственной — иначе дилер
 * возит дешевле материнской компании, и смысл схемы теряется.
 */
export function applyMarkup(base: number, markup: number, mode: number): number {
  const value = Math.max(0, markup);
  if (value === 0) return 0;
  return mode === MARKUP_PERCENT ? (base * value) / 100 : value;
}

export const DEFAULT_SETTINGS: CalcSettings = {
  complex: 180,
  certification: 150,
  commission: 280,
  swiftPercent: 1.5,
  freightInsurancePercent: 1.5,
  portHandling: 0,
  ecoFee: 0,
  marginDefault: 0,
  dealerDeliveryMarkup: 300,
  dealerDeliveryMarkupMode: MARKUP_USD,
  dealerComplexMarkup: 200,
  dealerComplexMarkupMode: MARKUP_USD,
};

// ─── Строки расчёта ──────────────────────────────────────────────────────────

export type LineKey =
  | 'bid'
  | 'delivery'
  | 'auctionFee'
  | 'portHandling'
  | 'freightInsurance'
  | 'customs'
  | 'ecoFee'
  | 'complex'
  | 'certification'
  | 'commission'
  | 'swift'
  | 'margin';

/** Откуда взялось значение строки — показывается цветной точкой в интерфейсе. */
export type LineSource =
  /** ввёл пользователь (ставка) */
  | 'input'
  /** из таблицы тарифов */
  | 'tariff'
  /** живой ответ baza-gai.com.ua */
  | 'api'
  /** последний успешный ответ API из кеша, «не обновлено» */
  | 'api-cached'
  /** локальная оценочная формула — API недоступен или нет ключа */
  | 'estimate'
  /** значение по умолчанию из настроек */
  | 'settings'
  /** вычисляется формулой (swift, страховка) */
  | 'computed'
  /** менеджер вписал руками поверх всего */
  | 'manual';

/** Группировка для сегментированной шкалы и клиентской карточки. */
export type LineGroup = 'bid' | 'deliveryAndFees' | 'customs' | 'services';

export const LINE_GROUP_LABELS: Record<LineGroup, string> = {
  bid: 'Ставка',
  deliveryAndFees: 'Доставка и сборы',
  customs: 'Растаможка',
  services: 'Сертификация и услуги',
};

export const LINE_GROUP_COLORS: Record<LineGroup, string> = {
  bid: '#7C848F',
  deliveryAndFees: '#4F8F86',
  customs: '#C49A52',
  services: '#7E7BAE',
};

export const LINE_GROUP_OF: Record<LineKey, LineGroup> = {
  bid: 'bid',
  delivery: 'deliveryAndFees',
  auctionFee: 'deliveryAndFees',
  portHandling: 'deliveryAndFees',
  freightInsurance: 'deliveryAndFees',
  customs: 'customs',
  ecoFee: 'customs',
  complex: 'services',
  certification: 'services',
  commission: 'services',
  swift: 'services',
  margin: 'services',
};

export const LINE_LABELS: Record<LineKey, string> = {
  bid: 'Ставка',
  delivery: 'Доставка',
  auctionFee: 'Аукционный сбор',
  portHandling: 'Портовые расходы',
  freightInsurance: 'Страховка фрахта',
  customs: 'Растаможка',
  ecoFee: 'Экологический сбор',
  complex: 'Комплекс',
  certification: 'Сертификация',
  commission: 'Комиссия',
  swift: 'Swift',
  margin: 'Маржа',
};

/** Порядок строк в интерфейсе расчёта. */
export const LINE_ORDER: LineKey[] = [
  'bid',
  'delivery',
  'auctionFee',
  'portHandling',
  'freightInsurance',
  'customs',
  'ecoFee',
  'complex',
  'certification',
  'commission',
  'swift',
  'margin',
];

export interface CalcLine {
  key: LineKey;
  label: string;
  group: LineGroup;
  source: LineSource;
  /** Итоговая сумма строки в USD (уже с учётом override и скидки) */
  amount: number;
  /** Базовое значение до ручной правки — чтобы показать «было / стало» */
  baseAmount: number | null;
  /** Строка учтена в итоге */
  enabled: boolean;
  /** Строка правится руками */
  editable: boolean;
  /** Видна только сотрудникам, в клиентскую карточку не попадает */
  internalOnly: boolean;
  /** Пояснение под строкой: «TX, седан · тариф $1 640 · −5%» */
  note: string | null;
}

// ─── Вход и выход движка расчёта ─────────────────────────────────────────────

export interface LotInfo {
  lotNumber: string | null;
  vin: string | null;
  makeModel: string | null;
  year: number | null;
  /** Объём двигателя в литрах, например 2.0 */
  engineVolume: number | null;
  /** Ёмкость батареи, кВт·ч — для электро */
  batteryPower: number | null;
  fuel: FuelType;
  platform: Platform;
  /** Штат / город аукциона */
  location: string;
  vehicleKind: VehicleKind;
}

export interface FxRates {
  /** Сколько UAH за 1 USD */
  usdUah: number;
  /** Сколько UAH за 1 EUR */
  eurUah: number;
  /** Сколько USD за 1 EUR — производная */
  eurUsd: number;
  fetchedAt: string;
  source: string;
  /** Курс зафиксирован менеджером вручную на этот расчёт */
  pinned: boolean;
}

export interface CalcInput {
  lot: LotInfo;
  /** Ставка на аукционе, USD */
  bid: number;
  /** Найденный тариф доставки, USD. null — тариф не найден */
  deliveryTariff: { amount: number; label: string } | null;
  /** Найденный аукционный сбор, USD */
  auctionFeeTariff: { amount: number; label: string } | null;
  /** Растаможка: значение и его происхождение */
  customs: { amount: number; source: Extract<LineSource, 'api' | 'api-cached' | 'estimate'> } | null;
  /** Персональная скидка менеджера к доставке, % */
  deliveryDiscountPercent: number;

  /**
   * Расчёт считает дилер — его персональные скидки с надбавок.
   *
   * `null` — считает материнская компания (администратор или менеджер), и
   * надбавка не применяется вовсе: она существует именно как цена для
   * дилера. Разделение на «null» и «нули» намеренное: ноль означает «дилер
   * без скидки, надбавку платит полностью», а это совсем другая цифра.
   *
   * Единицы те же, что у надбавки в настройках: доллары вычитаются из
   * долларов, проценты из процентов. Иначе «скидка 100» рядом с «надбавкой
   * 10%» не значит ничего.
   */
  dealer: { deliveryDiscount: number; complexDiscount: number } | null;
  settings: CalcSettings;
  /** Ручные правки поверх любой строки */
  overrides: Partial<Record<LineKey, number>>;
  /** Строки, выключенные в этом расчёте (страховка не нужна и т.п.) */
  disabled: LineKey[];
}

export interface CompositionSegment {
  group: LineGroup;
  label: string;
  amount: number;
  /** Доля от себестоимости, 0–100 */
  percent: number;
  color: string;
}

export interface CalcResult {
  lines: CalcLine[];
  /** Себестоимость — всё, кроме маржи */
  cost: number;
  /** Маржа, USD */
  margin: number;
  /** Цена клиенту = себестоимость + маржа */
  clientTotal: number;
  composition: CompositionSegment[];
  /**
   * Разбивка для клиента — построчно, без внутренней кухни.
   *
   * До 01.11 здесь лежали четыре группы («Доставка и сборы» одной суммой).
   * Заказчик попросил показывать те же строки, что он показывает клиенту
   * сам: ставка, сбор, доставка и так далее. Группа у строки осталась —
   * по ней красится сегментированная шкала и строится публичная витрина.
   */
  clientBreakdown: { key: LineKey; group: LineGroup; label: string; amount: number }[];
}

// ─── Растаможка ──────────────────────────────────────────────────────────────

export interface CustomsQuoteRequest {
  carType: CarType;
  price: number;
  currency: Currency;
  volume?: number | null;
  motor?: 'petrol' | 'diesel' | null;
  year?: number | null;
  power?: number | null;
}

export interface CustomsQuote {
  /** НДС */
  nds: number;
  /** Пошлина */
  duty: number;
  /** Акциз */
  excise: number;
  /** Пенсионный фонд */
  pensionFund: number;
  /** Сумма всех платежей (то, что попадает в строку «Растаможка») */
  totalFees: number;
  resultPriceUsd: number;
  resultPriceEur: number | null;
  resultPriceUah: number | null;
  currency: Currency;
  source: Extract<LineSource, 'api' | 'api-cached' | 'estimate'>;
  fetchedAt: string;
  /** Текст для интерфейса, если значение неактуально или оценочное */
  warning: string | null;
}

// ─── Витрина авто на публичном сайте ─────────────────────────────────────────

export type ShowcaseStatus =
  /** В пути / в наличии — можно купить сейчас */
  | 'available'
  /** Сейчас на торгах — готовы торговаться под клиента */
  | 'at_auction'
  /** Уже привезли: кейс с реальной итоговой ценой */
  | 'delivered_case';

export const SHOWCASE_STATUS_LABELS: Record<ShowcaseStatus, string> = {
  available: 'В дорозі / в наявності',
  at_auction: 'Зараз на торгах',
  delivered_case: 'Привезено — приклад розрахунку',
};

export interface ShowcasePhoto {
  id: string;
  url: string;
  sortOrder: number;
}

export interface ShowcaseItem {
  id: string;
  slug: string;
  status: ShowcaseStatus;
  title: string;
  makeModel: string;
  year: number | null;
  fuel: FuelType;
  engineVolume: number | null;
  vehicleKind: VehicleKind;
  platform: Platform;
  location: string;
  lotNumber: string | null;
  mileage: number | null;
  damage: string | null;
  /** Итоговая цена «под ключ», USD */
  turnkeyPriceUsd: number;
  /** Свёрнутая разбивка, которую видит клиент */
  breakdown: { label: string; amount: number }[];
  description: string | null;
  photos: ShowcasePhoto[];
  isPublished: boolean;
  publishedAt: string | null;
  /** Из какого расчёта опубликовано */
  calculationId: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Язык клиентских документов ──────────────────────────────────────────────

/**
 * Интерфейс приложения русский, публичный сайт украинский, а клиенту удобнее
 * получить документ на своём языке. Поэтому карточка и коммерческое
 * предложение переключаются отдельно от интерфейса.
 */
export type ClientLocale = 'ru' | 'uk';

export const LINE_GROUP_LABELS_UK: Record<LineGroup, string> = {
  bid: 'Ставка',
  deliveryAndFees: 'Доставка та збори',
  customs: 'Розмитнення',
  services: 'Сертифікація та послуги',
};

export function groupLabel(group: LineGroup, locale: ClientLocale): string {
  return locale === 'uk' ? LINE_GROUP_LABELS_UK[group] : LINE_GROUP_LABELS[group];
}

/**
 * Подписи строк так, как их видит клиент.
 *
 * Отдельно от `LINE_LABELS` по двум причинам. Во-первых, клиенту нужно
 * развёрнутое название: «Доставка до порта назначения» вместо «Доставка» и
 * «Комплекс (брокер, экспедитор, доставка до Львова)» вместо «Комплекс» —
 * в узкой внутренней таблице такие подписи не помещаются. Во-вторых, у
 * клиентского документа два языка, а у внутренней таблицы один.
 *
 * Формулировки взяты из правок заказчика от 01.11 дословно: именно этот
 * список он показывает клиенту в своём нынешнем инструменте.
 */
export const CLIENT_LINE_LABELS: Record<ClientLocale, Record<LineKey, string>> = {
  ru: {
    bid: 'Ставка',
    auctionFee: 'Аукционный сбор',
    delivery: 'Доставка до порта назначения',
    freightInsurance: 'Страховка фрахта',
    portHandling: 'Портовые расходы',
    customs: 'Растаможка',
    ecoFee: 'Экологический сбор',
    complex: 'Комплекс (брокер, экспедитор, доставка до Львова)',
    certification: 'Сертификация',
    commission: 'Комиссия компании',
    swift: 'Swift',
    // Маржа в клиентские документы не попадает никогда — подпись нужна
    // только для полноты записи, иначе TypeScript не примет Record
    margin: 'Маржа',
  },
  uk: {
    bid: 'Ставка',
    auctionFee: 'Аукційний збір',
    delivery: 'Доставка до порту призначення',
    freightInsurance: 'Страхування фрахту',
    portHandling: 'Портові витрати',
    customs: 'Розмитнення',
    ecoFee: 'Екологічний збір',
    complex: 'Комплекс (брокер, експедитор, доставка до Львова)',
    certification: 'Сертифікація',
    commission: 'Комісія компанії',
    swift: 'Swift',
    margin: 'Маржа',
  },
};

export function clientLineLabel(key: LineKey, locale: ClientLocale): string {
  return CLIENT_LINE_LABELS[locale][key];
}

/**
 * Подпись строки клиентской разбивки — с запасом на старые расчёты.
 *
 * Сохранённый расчёт хранит снимок результата, а до 01.11 разбивка была
 * групповой: у её строк есть `group`, но нет `key`. Такой снимок не
 * пересчитывается задним числом (тарифы с тех пор другие, и цифра в истории
 * обязана остаться той, что видел клиент), поэтому подпись ему достаётся по
 * группе. Без этого запаса просмотр старого расчёта показывал суммы вообще
 * без подписей — проверено глазами, ошибки при этом не было ни одной.
 */
export function clientBreakdownLabel(
  row: { key?: LineKey; group: LineGroup },
  locale: ClientLocale,
): string {
  return row.key ? clientLineLabel(row.key, locale) : groupLabel(row.group, locale);
}

/**
 * Порядок строк в клиентской разбивке — по правкам заказчика от 01.11.
 *
 * Отличается от `LINE_ORDER`: клиенту сбор идёт сразу за ставкой (это две
 * части цены самого лота), а доставка с портовыми расходами — следом.
 *
 * `margin` в списке нет вовсе: она сидит внутри доставки. `ecoFee` есть,
 * хотя заказчик его не назвал, — без него сумма строк перестала бы сходиться
 * с итогом у тех расчётов, где сбор не ноль, и клиент увидел бы разницу,
 * которую никто не может объяснить. При нулевом значении строка всё равно
 * не показывается, и список выходит ровно тот, что в правках.
 */
export const CLIENT_LINE_ORDER: LineKey[] = [
  'bid',
  'auctionFee',
  'delivery',
  'freightInsurance',
  'portHandling',
  'customs',
  'ecoFee',
  'complex',
  'certification',
  'commission',
  'swift',
];

/** Подписи клиентских документов. Только то, что реально видит клиент. */
export const CLIENT_STRINGS: Record<ClientLocale, {
  offerTitle: string;
  total: string;
  vehicle: string;
  lot: string;
  year: string;
  fuel: string;
  volume: string;
  location: string;
  breakdown: string;
  validUntil: string;
  rate: string;
  disclaimer: string;
  contacts: string;
}> = {
  ru: {
    offerTitle: 'Коммерческое предложение',
    total: 'Итого под ключ',
    vehicle: 'Автомобиль',
    lot: 'Лот / VIN',
    year: 'Год выпуска',
    fuel: 'Топливо',
    volume: 'Объём двигателя',
    location: 'Площадка',
    breakdown: 'Из чего складывается цена',
    validUntil: 'Расчёт действителен до',
    rate: 'Курс расчёта',
    disclaimer:
      'Цена включает доставку, сборы, растаможку и оформление. ' +
      'Окончательная сумма фиксируется после подтверждения лота на аукционе.',
    contacts: 'Контакты',
  },
  uk: {
    offerTitle: 'Комерційна пропозиція',
    total: 'Разом під ключ',
    vehicle: 'Автомобіль',
    lot: 'Лот / VIN',
    year: 'Рік випуску',
    fuel: 'Пальне',
    volume: 'Об’єм двигуна',
    location: 'Майданчик',
    breakdown: 'З чого складається ціна',
    validUntil: 'Розрахунок дійсний до',
    rate: 'Курс розрахунку',
    disclaimer:
      'Ціна включає доставку, збори, розмитнення та оформлення. ' +
      'Остаточна сума фіксується після підтвердження лоту на аукціоні.',
    contacts: 'Контакти',
  },
};

export const FUEL_LABELS_UK: Record<FuelType, string> = {
  petrol: 'Бензин',
  diesel: 'Дизель',
  electric: 'Електро',
  hybrid: 'Гібрид',
};

export function fuelLabel(fuel: FuelType, locale: ClientLocale): string {
  return locale === 'uk' ? FUEL_LABELS_UK[fuel] : FUEL_LABELS[fuel];
}

// ─── Агент ───────────────────────────────────────────────────────────────────

export interface AgentProfile {
  id: string;
  login: string;
  fullName: string;
  /** Имя, которое видит клиент на сайте агента */
  publicName: string | null;
  phone: string | null;
  telegram: string | null;
  referralCode: string | null;
  commissionType: CommissionType;
  commissionValue: number;
  /**
   * Персональные скидки дилера с общих надбавок (ТЗ от 01.11).
   *
   * Вычитаются из `dealerDeliveryMarkup` и `dealerComplexMarkup` в
   * настройках, в тех же единицах. Правит их только материнский админ:
   * «когда конкретный дилер выходит на объёмы, старший дилер может
   * увеличивать скидку».
   */
  dealerDeliveryDiscount: number;
  dealerComplexDiscount: number;
  isActive: boolean;
  domains: { id: string; host: string; isActive: boolean }[];
  createdAt: string;
}

export interface AgentStats {
  /** Всего сделок дилера — колонка «Сделок» в его таблице */
  dealsTotal: number;
  /**
   * Переплата: разница между плановой суммой доставки и фактически
   * оплаченной, по всем его сделкам. Кто кому должен при разнице в плюс,
   * заказчик ещё не ответил, поэтому это просто число без выводов.
   */
  overpayUsd: number;
  /**
   * Дата последнего обнуления переплаты. С неё счёт идёт заново — так
   * просил ТЗ: «после этой даты бухгалтерия ведётся дальше». `null` —
   * обнулений ещё не было, и переплата считается за всё время.
   */
  overpayResetAt: string | null;
  leadsTotal: number;
  leadsNew: number;
  calculations: number;
  dealsWon: number;
  dealsLost: number;
  commissionEarned: number;
  commissionPaid: number;
  commissionPending: number;
}

/** Показатели дилера за период — месяц, квартал или год. */
export interface AgentPeriodStats {
  deals: number;
  earnedUsd: number;
  overpayUsd: number;
}

/** Публичная карточка агента для его сайта — ничего внутреннего. */
export interface AgentPublicCard {
  publicName: string;
  phone: string | null;
  telegram: string | null;
  referralCode: string | null;
}
